<template>
  <form
    class="wpcf7-form contact-inquiry-form"
    :class="{ 'contact-inquiry-form--footer': variant === 'footer' }"
    novalidate
    @submit.prevent="onSubmit"
  >
    <p v-if="variant === 'page'" class="cf7-title">
      <i class="fa fa-envelope-o" aria-hidden="true" />
      {{ $t('form.pageHint') }}
    </p>

    <p>
      <input
        v-model="form.name"
        type="text"
        name="your-name"
        class="wpcf7-form-control wpcf7-text"
        :class="{ 'field-invalid': errors.name }"
        :placeholder="$t('form.name')"
        aria-required="true"
        :aria-invalid="errors.name ? 'true' : 'false'"
        @input="clearError('name')"
      />
      <span v-if="errors.name" class="field-error" role="alert">{{ errors.name }}</span>
    </p>

    <p>
      <input
        v-model="form.email"
        type="email"
        name="your-email"
        class="wpcf7-form-control wpcf7-text wpcf7-email"
        :class="{ 'field-invalid': errors.email }"
        :placeholder="$t('form.email')"
        aria-required="true"
        :aria-invalid="errors.email ? 'true' : 'false'"
        @input="clearError('email')"
      />
      <span v-if="errors.email" class="field-error" role="alert">{{ errors.email }}</span>
    </p>

    <p>
      <input
        v-model="form.website"
        type="text"
        name="your-subject"
        class="wpcf7-form-control wpcf7-text"
        :class="{ 'field-invalid': errors.website }"
        :placeholder="$t('form.website')"
        aria-required="true"
        :aria-invalid="errors.website ? 'true' : 'false'"
        @input="clearError('website')"
      />
      <span v-if="errors.website" class="field-error" role="alert">{{ errors.website }}</span>
    </p>

    <p>
      <textarea
        v-model="form.message"
        name="your-message"
        cols="40"
        rows="10"
        class="wpcf7-form-control wpcf7-textarea"
        :class="{ 'field-invalid': errors.message }"
        :placeholder="$t('form.message')"
        aria-required="true"
        :aria-invalid="errors.message ? 'true' : 'false'"
        @input="clearError('message')"
      />
      <span v-if="errors.message" class="field-error" role="alert">{{ errors.message }}</span>
    </p>

    <p>
      <input
        type="submit"
        :value="submitting ? $t('common.sending') : $t('common.send')"
        class="wpcf7-form-control wpcf7-submit"
        :disabled="submitting"
      />
    </p>

    <p
      v-if="submitMessage"
      class="contact-form-notice"
      :class="{ 'contact-form-notice--error': submitError }"
      role="status"
    >
      {{ submitMessage }}
    </p>
  </form>
</template>

<script>
import { submitContactInquiry } from '@/api/inquiries'
import { formValidationMessages } from '@/i18n'
import { validateContactInquiryForm } from '@/utils/contactFormValidation'
import { openContactMailto } from '@/utils/sendContactMailto'

const EMPTY_FORM = () => ({
  name: '',
  email: '',
  website: '',
  message: ''
})

export default {
  name: 'ContactInquiryForm',
  props: {
    variant: {
      type: String,
      default: 'page',
      validator: (v) => ['page', 'footer'].includes(v)
    }
  },
  data() {
    return {
      form: EMPTY_FORM(),
      errors: {},
      submitMessage: '',
      submitError: false,
      submitting: false
    }
  },
  methods: {
    clearError(field) {
      if (this.errors[field]) {
        const next = { ...this.errors }
        delete next[field]
        this.errors = next
      }
    },
    resetForm() {
      this.form = EMPTY_FORM()
      this.errors = {}
    },
    async onSubmit() {
      this.submitMessage = ''
      const { valid, errors, values } = validateContactInquiryForm(
        this.form,
        formValidationMessages()
      )
      this.errors = errors
      if (!valid) {
        this.submitError = true
        this.submitMessage = this.$t('form.fixErrors')
        return
      }

      this.submitting = true
      this.submitError = false
      try {
        await submitContactInquiry(values)
        openContactMailto(values)
        this.submitMessage = this.$t('form.success')
        this.resetForm()
      } catch (err) {
        this.submitError = true
        this.submitMessage = err.message || this.$t('form.submitError')
      } finally {
        this.submitting = false
      }
    }
  }
}
</script>

<style scoped>
.field-error {
  display: block;
  margin-top: 6px;
  font-size: 16px;
  line-height: 1.4;
  color: #c62828;
}

.field-invalid {
  border-color: #e57373 !important;
}

.contact-form-notice {
  margin: 0;
  padding: 10px 12px;
  font-size: 17px;
  background: #f0f8e8;
  border: 1px solid #c5e1a5;
  color: #33691e;
}

.contact-form-notice--error {
  background: #fdecea;
  border-color: #f5c6cb;
  color: #c62828;
}

.contact-inquiry-form--footer .field-error {
  color: #ffab91;
}

.contact-inquiry-form--footer .field-invalid {
  border-color: #ff8a80 !important;
}
</style>
